// Badge Progress Ring Component
// Renders a circular progress ring with level number or completion status

const BADGE_CONFIGS = {
  trader: {
    label: 'Trader',
    thresholds: [5, 10, 25, 50, 100], // Level 1-5
    getCount: (data) => data.completedTrades || 0,
    description: 'Complete trades to level up',
    levelDescriptions: [
      'Complete 5 trades to reach Level 1',
      'Complete 10 trades to reach Level 2',
      'Complete 25 trades to reach Level 3',
      'Complete 50 trades to reach Level 4',
      'Complete 100 trades to reach Level 5',
    ],
  },
  officer: {
    label: 'Officer',
    thresholds: [5, 10, 25, 50, 100],
    getCount: (data) => data.successfulReports || 0,
    description: 'Successful reports to level up',
    levelDescriptions: [
      'Get 5 accepted reports to reach Level 1',
      'Get 10 accepted reports to reach Level 2',
      'Get 25 accepted reports to reach Level 3',
      'Get 50 accepted reports to reach Level 4',
      'Get 100 accepted reports to reach Level 5',
    ],
  },
  researcher: {
    label: 'Researcher',
    thresholds: [5, 10, 25, 50, 100],
    getCount: (data) => data.imageContributions || 0,
    description: 'Contribute card images to level up',
    levelDescriptions: [
      'Contribute 5 card images to reach Level 1',
      'Contribute 10 card images to reach Level 2',
      'Contribute 25 card images to reach Level 3',
      'Contribute 50 card images to reach Level 4',
      'Contribute 100 card images to reach Level 5',
    ],
  },
  collector: {
    label: 'Collector',
    target: 100,
    getCount: (data) => data.totalCards || 0,
    description: 'Collect 100 cards',
    oneOff: true,
  },
  organized: {
    label: 'Organizer',
    target: 10,
    getCount: (data) => data.qualifyingBinders || 0,
    description: '10 binders with 10+ cards each',
    oneOff: true,
  },
  charter: {
    label: 'Charter Member',
    target: 1,
    getCount: (data) => data.charterMember || 0,
    description: 'Complete the CardChase Challenge',
    oneOff: true,
  },
};

const LEVEL_COLORS = [
  '#4a5568', // pre-level (gray)
  '#0ea5e9', // Level 1 (cyan)
  '#8b5cf6', // Level 2 (purple)
  '#f59e0b', // Level 3 (amber)
  '#22c55e', // Level 4 (green)
  '#ef4444', // Level 5 (red)
];

function getBadgeState(type, data) {
  const config = BADGE_CONFIGS[type];
  if (!config) return null;

  const count = config.getCount(data);

  if (config.oneOff) {
    const target = config.target;
    const progress = Math.min(count / target, 1);
    return {
      type,
      label: config.label,
      description: config.description,
      count,
      level: count >= target ? 'completed' : null,
      progress,
      displayText: count >= target ? null : `${count}/${target}`,
      color: count >= target ? '#22c55e' : '#4a5568',
      completed: count >= target,
      nextTarget: target,
    };
  }

  // Leveled badge
  const thresholds = config.thresholds;
  let level = 0;
  for (let i = 0; i < thresholds.length; i++) {
    if (count >= thresholds[i]) {
      level = i + 1;
    }
  }

  const maxLevel = thresholds.length;
  const isMaxed = level >= maxLevel;

  let progress = 0;
  let nextTarget = thresholds[0];

  if (level === 0) {
    // Pre-level: progress toward first threshold
    progress = count / thresholds[0];
    nextTarget = thresholds[0];
  } else if (!isMaxed) {
    // Between levels: progress from current threshold to next
    const currentThreshold = thresholds[level - 1];
    nextTarget = thresholds[level];
    progress = (count - currentThreshold) / (nextTarget - currentThreshold);
  } else {
    // Maxed out
    progress = 1;
    nextTarget = thresholds[maxLevel - 1];
  }

  const color = LEVEL_COLORS[level] || LEVEL_COLORS[LEVEL_COLORS.length - 1];

  let displayText;
  if (level === 0) {
    displayText = `${count}/${thresholds[0]}`;
  } else {
    displayText = String(level);
  }

  const levelDesc = level === 0
    ? config.levelDescriptions[0]
    : level < maxLevel
      ? config.levelDescriptions[level]
      : `Max level reached (${count} total)`;

  return {
    type,
    label: config.label,
    description: config.description,
    levelDescription: levelDesc,
    count,
    level,
    maxLevel,
    progress: Math.min(progress, 1),
    displayText,
    color,
    completed: isMaxed,
    nextTarget,
    isMaxed,
  };
}

const CIRCUMFERENCE = 2 * Math.PI * 15.5; // ~97.4

function BadgeProgressRing({ type, data, size = 70, showLabel = true, theme }) {
  const state = getBadgeState(type, data);
  if (!state) return null;

  const offset = CIRCUMFERENCE * (1 - state.progress);
  const isCompleted = state.completed;
  const hasLevel = state.level && state.level !== 'completed';

  const glowStyle = isCompleted || hasLevel
    ? { filter: `drop-shadow(0 0 6px ${state.color}50)` }
    : {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox="0 0 36 36"
          style={glowStyle}
        >
          {/* Background ring */}
          <circle
            cx="18" cy="18" r="15.5"
            fill="none"
            stroke={theme?.border || '#2a2a2a'}
            strokeWidth="3"
          />
          {/* Progress ring */}
          <circle
            cx="18" cy="18" r="15.5"
            fill="none"
            stroke={state.color}
            strokeWidth="3"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 18 18)"
          />
        </svg>
        {/* Center content */}
        <span style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: state.level === 'completed'
            ? size * 0.37
            : state.level === 0 || state.level === null
              ? size * 0.2
              : size * 0.31,
          fontWeight: 800,
          color: state.level === 'completed'
            ? '#22c55e'
            : state.level === 0 || state.level === null
              ? theme?.textSecondary || '#6a6a6a'
              : state.color,
        }}>
          {state.level === 'completed' ? '\u2713' : state.displayText}
        </span>
      </div>
      {showLabel && (
        <span style={{
          fontSize: 11,
          fontWeight: 500,
          color: theme?.textSecondary || '#6a6a6a',
        }}>
          {state.label}
        </span>
      )}
    </div>
  );
}

export { BadgeProgressRing, getBadgeState, BADGE_CONFIGS, LEVEL_COLORS };
